import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import { ProviderHeader, Text, StyledCard, StyledCardContent } from '../../components/index';

import { MessageListWindow } from '../../components/Message/MessengerWindow';
import ChatWindow from '../../components/Message/ChatWindow';

import { providerPageWidth } from '../../constant';

import { readMessage } from '../../services/index';
import { GET_STAFF_MESSAGES } from '../../services/provider';

import { decrypt } from '../../utils/messages';

import { alertError, setMessagesReceiver, clearMessagesReceiver } from '../../redux/stateActions';

import socket from '../../socket';

const Message = ({ history }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const messages = useSelector((state) => state.messages);

    const [selectedTab, setSelectedTab] = useState(0);

    /*---------- Messenger Window ----------*/
    const [searchValue, setSearchValue] = useState('');
    const [messagesData, setMessagesData] = useState([]);

    const {
        data: getMessagesData,
        error: getMessagesError,
        refetch: getMessageRefetch,
    } = useQuery(GET_STAFF_MESSAGES, {
        variables: { id: user.staff.id },
        skip: _.isEmpty(user),
    });
    useEffect(() => {
        if (getMessagesError) {
            dispatch(alertError(`Cannot get Messages: ${getMessagesError.message}`));
        }
        if (getMessagesData) {
            let decryptMessages = getMessagesData.staff.messages.map((message) =>
                message.text
                    ? {
                          ...message,
                          isRead: ['Provider', 'Provider Admin', 'Admin'].includes(
                              message.senderRole
                          )
                              ? true
                              : message.isRead,
                          text:
                              !message?.staff?.id || !message?.patient?.id
                                  ? 'text unavailable'
                                  : decrypt(
                                        message.text,
                                        ['Provider', 'Provider Admin', 'Admin'].includes(
                                            message.senderRole
                                        )
                                            ? message.staff.id + message.patient.id
                                            : message.patient.id + message.staff.id
                                    ),
                      }
                    : {
                          ...message,
                          isRead: ['Provider', 'Provider Admin', 'Admin'].includes(
                              message.senderRole
                          )
                              ? true
                              : message.isRead,
                      }
            );

            if (selectedTab === 1) {
                decryptMessages = decryptMessages.filter((m) => m.isRead === false);
            }

            if (searchValue === '') {
                setMessagesData(decryptMessages);
            } else {
                const filteredDecryptMessages = decryptMessages.filter((data) =>
                    data?.patient?.user?.fullname.toLowerCase().includes(searchValue.toLowerCase())
                );
                setMessagesData(filteredDecryptMessages);
            }
        }
    }, [getMessagesError, getMessagesData, searchValue, selectedTab]);

    const handleClickMessage = async (data) => {
        if (data.id !== messages.id) {
            dispatch(setMessagesReceiver(data));
        }

        if (data.isRead === true) return;

        try {
            const res = await readMessage(data.messageId);

            if (res.status === 200) {
                getMessageRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`cannot update message: ${error.message}`));
        }
    };

    useEffect(() => {
        socket.on('newMessage', () => {
            getMessageRefetch();
        });

        // fire when change route
        const unlisten = history.listen(() => {
            dispatch(clearMessagesReceiver());
        });
        return function cleanup() {
            unlisten();
        };
    }, []);

    return (
        <div
            id="message-container"
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <ProviderHeader>
                <Text $fontSize="28px" $bold>
                    Message
                </Text>
            </ProviderHeader>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '40% 60%',
                    columnGap: '15px',
                    height: '100%',
                    maxHeight: '720px',
                }}
            >
                <StyledCard padding="15px 0 10px">
                    <StyledCardContent style={{ height: '100%' }}>
                        <div
                            style={{
                                marginLeft: '25px',
                                display: 'flex',
                                columnGap: '10px',
                            }}
                        >
                            <Text
                                $color={selectedTab === 0 ? 'black' : 'gray2'}
                                $bold={selectedTab === 0}
                                $medium={selectedTab !== 0}
                                onClick={() => setSelectedTab(0)}
                            >
                                All Messages
                            </Text>
                            <Text
                                $color={selectedTab === 1 ? 'black' : 'gray2'}
                                $bold={selectedTab === 1}
                                $medium={selectedTab !== 1}
                                onClick={() => setSelectedTab(1)}
                            >
                                Unread
                            </Text>
                        </div>
                        <MessageListWindow
                            value={searchValue}
                            onChange={setSearchValue}
                            data={messagesData}
                            onClick={handleClickMessage}
                            myRole="provider"
                            $width="100%"
                        />
                    </StyledCardContent>
                </StyledCard>
                <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden' }}>
                    {messages?.id === null ? (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Text $color="gray2">Please Select a Message</Text>
                        </div>
                    ) : (
                        <ChatWindow fullScreen hideGoBack open={true} />
                    )}
                </div>
            </div>
        </div>
    );
};

Message.propTypes = {
    history: PropTypes.object,
};

export default Message;
